import { z } from "zod";

/** Os quadros que podem ocupar as posições da aba Início da ficha do paciente. */
export const CHAVES_DOS_QUADROS = [
  "consultas",
  "proteses",
  "tratamentos",
  "alertas",
  "atendimentos",
  "arquivos",
  "contato",
] as const;
export type ChaveDoQuadro = (typeof CHAVES_DOS_QUADROS)[number];

export const QUANTIDADE_DE_QUADROS = 4;

/** O que aparece pra quem nunca trocou nada, nesta ordem. */
export const QUADROS_PADRAO: ChaveDoQuadro[] = ["consultas", "proteses", "tratamentos", "alertas"];

/**
 * Preferência salva em `User.quadrosDaFicha`. Na leitura aceita qualquer texto (chave que saiu do
 * produto não invalida a preferência inteira — `resolverQuadros` descarta); na gravação, só chave
 * conhecida e exatamente uma por posição.
 */
export const quadrosDaFichaSalvosSchema = z.array(z.string().min(1).max(40)).max(QUANTIDADE_DE_QUADROS);
export const quadrosDaFichaSchema = z
  .array(z.enum(CHAVES_DOS_QUADROS))
  .length(QUANTIDADE_DE_QUADROS)
  .refine((l) => new Set(l).size === l.length, { message: "Cada quadro aparece uma vez só." });

/** `null` volta ao padrão. */
export const salvarQuadrosDaFichaSchema = quadrosDaFichaSchema.nullable();

/**
 * Os quadros de cada posição. Sem preferência, o padrão na ordem, pulando o que a pessoa não pode
 * ver. Com preferência, cada escolha fica onde a pessoa pôs; posição com quadro que não existe
 * mais, que ela não pode ver ou repetido recebe o primeiro do padrão (e depois o primeiro
 * disponível) que ainda não está na tela — a grade nunca fica com buraco.
 */
export function resolverQuadros(
  salvos: readonly string[] | null | undefined,
  disponiveis: readonly ChaveDoQuadro[],
): ChaveDoQuadro[] {
  const pode = new Set(disponiveis);
  const reserva = [...new Set([...QUADROS_PADRAO, ...CHAVES_DOS_QUADROS])].filter((c) => pode.has(c));
  if (!salvos || salvos.length === 0) return reserva.slice(0, QUANTIDADE_DE_QUADROS);
  const base = salvos;
  const escolhidos: (ChaveDoQuadro | null)[] = [];
  const usados = new Set<ChaveDoQuadro>();
  for (let i = 0; i < QUANTIDADE_DE_QUADROS; i++) {
    const c = base[i] as ChaveDoQuadro | undefined;
    if (c && pode.has(c) && !usados.has(c)) {
      escolhidos.push(c);
      usados.add(c);
    } else {
      escolhidos.push(null);
    }
  }
  return escolhidos
    .map((c) => {
      if (c) return c;
      const proximo = reserva.find((r) => !usados.has(r));
      if (proximo) usados.add(proximo);
      return proximo ?? null;
    })
    .filter((c): c is ChaveDoQuadro => !!c);
}
